import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, Star } from "lucide-react";
import ReviewList from "../components/ReviewList";
import RelatedProducts from "../components/RelatedProducts";
import { fetchProductBySlug, fetchAllProducts } from "../api/product";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { getReviewsByProductId, postReview } from "../api/review";
import { toast } from "react-toastify";
import ReviewForm from "../components/ReviewForm";
import SEO from "../api/seo";

const IMAGE_BASE_URL =
  process.env.REACT_APP_API_IMAGE_URL || "http://localhost:3000";

const ProductDetailsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const { token } = useAuth();
  const user = JSON.parse(localStorage.getItem("user"));
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const images = product?.image || [];
  const [activeImage, setActiveImage] = useState(images[0]);
  const [selectedPriceType, setSelectedPriceType] = useState("new");

  useEffect(() => {
    if (product && product.image && product.image.length > 0) {
      setActiveImage(product.image[0]);
    }
  }, [product]);

  useEffect(() => {
    const getProductAndReviews = async () => {
      setLoading(true);
      try {
        const productData = await fetchProductBySlug(slug);
        setProduct(productData);

        if (productData && productData._id) {
          await fetchAndSetReviews(productData._id);
        }

        const { data: allProducts } = await fetchAllProducts();

        const related = allProducts.filter(
          (p) =>
            p._id !== productData._id &&
            p.category_id === productData.category_id
        );

        setRelatedProducts(related.slice(0, 3));
      } catch (err) {
        console.error("Product or review fetch error:", err);
        toast.error("Failed to load product details or reviews.");
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    getProductAndReviews();
  }, [slug]);

  const fetchAndSetReviews = async (productId) => {
    try {
      const fetchedReviews = await getReviewsByProductId(productId);
      setReviews(Array.isArray(fetchedReviews.data) ? fetchedReviews.data : []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
      toast.error("Failed to load product reviews.");
    }
  };

  const handleAddToCart = () => {
    if (
      !product ||
      product.status !== "Active" ||
      Number(product.quantity) <= 0
    ) {
      toast.warn("This product is out of stock or unavailable.");
      return;
    }

    const selectedPrice =
      selectedPriceType === "preowned" ? product.preowned_price : product.price;

    const productWithPrice = {
      ...product,
      selectedPriceType,
      price: selectedPrice, // override with the selected one
    };

    addToCart(productWithPrice, quantity);
  };

  const increaseQuantity = () => {
    if (
      product &&
      Number(product.quantity) &&
      quantity < Number(product.quantity)
    ) {
      setQuantity((prev) => prev + 1);
    } else if (product && product.quantity === 0) {
      toast.warn("Product is out of stock.");
    } else {
      toast.info("Maximum available quantity reached.");
    }
  };

  const decreaseQuantity = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-black text-white">
        <p className="text-gray-400">Loading product...</p>
      </section>
    );
  }

  if (!product) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-black text-white">
        <p className="text-red-400">Product not found or an error occurred.</p>
      </section>
    );
  }

  const isNewOutOfStock =
    product.status !== "Active" || Number(product.quantity) <= 0;

  const isPreownedOutOfStock =
    product.preowned_status !== "Active" ||
    Number(product.preowned_quantity) <= 0;

  const isOutOfStock =
    selectedPriceType === "preowned" ? isPreownedOutOfStock : isNewOutOfStock;

  return (
    <>
      <SEO
        title={product.meta_title || product.name}
        description={product.meta_description || product.description}
        image={`${IMAGE_BASE_URL}${product.image}`}
      />
      <section className="min-h-screen px-6 pt-32 pb-20 bg-black text-white relative overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 items-start mt-10">
            {/* Image Block */}
        {/* Image / Video Block */}
<div className="w-full lg:w-[40%] flex flex-col items-center">
  {/* Main Media (Portrait 3:4 ratio) */}
  <div className="rounded-2xl overflow-hidden shadow-xl shadow-cyan-500/10 border border-cyan-600/10 bg-black/10 flex items-center justify-center aspect-[3/4] w-[350px] max-w-full">
    {activeImage && /\.(mp4|mov|avi|mkv)$/i.test(activeImage) ? (
      <video
        src={`${IMAGE_BASE_URL}${activeImage}`}
        controls
        className="w-full h-full object-contain transition-all duration-300 bg-black"
        onError={(e) => {
          e.target.onerror = null;
          e.target.poster =
            "https://placehold.co/600x800/000000/FFFFFF?text=Video+Not+Found";
        }}
      />
    ) : (
      <img
        src={`${IMAGE_BASE_URL}${activeImage}`}
        alt={product.name}
        className="w-full h-full object-contain transition-all duration-300"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src =
            "https://placehold.co/600x800/000000/FFFFFF?text=Image+Not+Found";
        }}
      />
    )}
  </div>

  {/* Thumbnail Images/Videos */}
  {images.length > 1 && (
    <div className="flex justify-center gap-4 mt-4 flex-wrap">
      {images.slice(0, 4).map((img, idx) => {
        const isVideo = /\.(mp4|mov|avi|mkv)$/i.test(img);

        return (
          <div
            key={idx}
            className={`w-20 aspect-[3/4] rounded-xl overflow-hidden border-2 cursor-pointer transition-all duration-200 bg-black/20 flex items-center justify-center ${
              activeImage === img
                ? "border-cyan-500 ring-2 ring-cyan-500/50"
                : "border-transparent hover:border-cyan-500/40"
            }`}
            onClick={() => setActiveImage(img)}
          >
            {isVideo ? (
              <video
                src={`${IMAGE_BASE_URL}${img}`}
                className="w-full h-full object-cover"
                muted
                playsInline
                preload="metadata"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.poster =
                    "https://placehold.co/150x200/000000/FFFFFF?text=No+Video";
                }}
              />
            ) : (
              <img
                src={`${IMAGE_BASE_URL}${img}`}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://placehold.co/150x200/000000/FFFFFF?text=No+Img";
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  )}
</div>

            <div className="flex-1 space-y-6">
              {product.isBestSeller === "Yes" && (
                <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-cyan-600 to-purple-600 text-white shadow-md shadow-cyan-400/20 tracking-wider">
                  ⚡ BEST SELLER
                </span>
              )}

              {/* Product Name */}
              <h1 className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                {product.name}
              </h1>

              {/* Dynamic Price */}
              <p className="text-2xl font-bold text-white">
                ₹
                {Number(
                  selectedPriceType === "preowned"
                    ? product.preowned_price
                    : product.price
                ).toLocaleString()}
              </p>

              {/* Type Selector Buttons */}
              <div className="flex gap-4 mt-4">
                <button
                  onClick={() => setSelectedPriceType("new")}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300
        ${
          selectedPriceType === "new"
            ? "text-white bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 scale-105"
            : "text-cyan-300 border border-cyan-400 hover:bg-gradient-to-r hover:from-cyan-400 hover:via-blue-500 hover:to-purple-600 hover:text-white"
        }`}
                >
                  Brand New
                </button>

                <button
                  onClick={() => setSelectedPriceType("preowned")}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300
        ${
          selectedPriceType === "preowned"
            ? "text-white bg-gradient-to-r from-pink-400 via-fuchsia-500 to-purple-600 scale-105"
            : "text-pink-300 border border-pink-400 hover:bg-gradient-to-r hover:from-pink-400 hover:via-fuchsia-500 hover:to-purple-600 hover:text-white"
        }`}
                >
                  Pre-Owned
                </button>
              </div>

              {/* Quantity Control */}
              <div className="flex items-center gap-4">
                <button
                  onClick={decreaseQuantity}
                  className="p-2 bg-cyan-800 rounded-full hover:bg-cyan-700 transition-colors duration-200"
                  aria-label="Decrease quantity"
                >
                  <Minus size={16} />
                </button>

                <span className="text-lg font-semibold">{quantity}</span>

                <button
                  onClick={increaseQuantity}
                  className="p-2 bg-cyan-800 rounded-full hover:bg-cyan-700 transition-colors duration-200"
                  aria-label="Increase quantity"
                >
                  <Plus size={16} />
                </button>
              </div>

              {/* Add to Cart */}
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                className={`px-8 py-4 rounded-2xl text-lg font-semibold flex items-center gap-3 transition-all duration-300
    ${
      isOutOfStock
        ? "bg-gray-600 cursor-not-allowed text-gray-300"
        : selectedPriceType === "new"
        ? "bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-cyan-500 hover:to-purple-600 shadow-lg hover:shadow-cyan-400/30 text-white"
        : "bg-gradient-to-r from-pink-500 to-purple-600 hover:from-purple-600 hover:to-pink-500 shadow-lg hover:shadow-pink-400/30 text-white"
    }`}
              >
                <ShoppingCart size={22} />
                {isOutOfStock ? "Out of Stock" : "Add to Cart"}
              </button>

              {/* Description */}
              <p className="text-gray-300 leading-relaxed max-w-xl">
                {product.description}
              </p>

              {/* Product Details */}
              <div className="grid grid-cols-2 gap-6 pt-6 text-sm max-w-xl">
                <div>
                  <span className="text-gray-500">Category:</span>
                  <p>{product.category}</p>
                </div>

                <div>
                  <span className="text-gray-500">Price:</span>
                  <p>
                    ₹
                    {Number(
                      selectedPriceType === "preowned"
                        ? product.preowned_price
                        : product.price
                    ).toLocaleString()}
                  </p>
                </div>

                <div>
                  <span className="text-gray-500">Status:</span>
                  <p
                    className={
                      (selectedPriceType === "preowned"
                        ? product.preowned_status
                        : product.status) === "Active"
                        ? "text-green-400 font-semibold"
                        : "text-red-500 font-semibold"
                    }
                  >
                    {(selectedPriceType === "preowned"
                      ? product.preowned_status
                      : product.status) === "Active"
                      ? "In Stock"
                      : "Out of Stock"}
                  </p>
                </div>

                <div>
                  <span className="text-gray-500">Quantity:</span>
                  <p>
                    {selectedPriceType === "preowned"
                      ? product.preowned_quantity
                      : product.quantity}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Review Submission Form */}
          <div className="p-6 bg-gray-950 text-white rounded-2xl shadow-xl border border-purple-700 my-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 mb-4 sm:mb-0">
                Customer Reviews
              </h2>

              <button
                onClick={() => setShowReviewModal(true)}
                className="px-5 py-3 rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 hover:from-purple-600 hover:to-pink-500 text-white font-semibold shadow-lg"
              >
                Write a Review
              </button>
            </div>

            {/* Review Modal */}
            {showReviewModal && (
              <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                <div className="bg-gray-900 p-8 rounded-2xl border border-purple-700 shadow-2xl max-w-lg w-full relative">
                  <button
                    className="absolute top-3 right-4 text-gray-400 hover:text-pink-500 text-xl"
                    onClick={() => setShowReviewModal(false)}
                  >
                    ✕
                  </button>
                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-6">
                    Write a Review
                  </h3>
                  <ReviewForm
                    product={product}
                    user={user}
                    token={token}
                    fetchAndSetReviews={fetchAndSetReviews}
                  />
                </div>
              </div>
            )}

            {/* Review List */}
            <div className="mt-10">
              <ReviewList reviews={reviews} />
            </div>
          </div>

          {/* Related Products */}
          <div>
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-500 mb-6">
              Related Products
            </h2>
            <RelatedProducts products={relatedProducts} />
            {/* You might want to fetch actual related products here */}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductDetailsPage;
